import React, { useEffect, useMemo, useState } from "react";
import { ClipboardList, Check, X, ArrowUpRight, Plane, Users, HelpCircle, GraduationCap, Laptop, MoreHorizontal } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNavigate } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_ICON = {
  "Leave Request": Plane,
  "Meeting Request": Users,
  "Assistance Request": HelpCircle,
  "Training Request": GraduationCap,
  "Equipment Request": Laptop,
  "Other": MoreHorizontal,
};

const initials = (name) => (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
const fmt = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

export default function PendingRequestsCard({ refreshKey, onChange }) {
  const [user, setUser] = useState({ name: "" });
  const [requests, setRequests] = useState([]);
  const navigate = useNavigate();

  const load = () => endpoints.listEmployeeRequests({ status: "Pending" }).then(setRequests).catch(() => {});
  useEffect(() => {
    endpoints.me().then(setUser).catch(() => {});
    load();
  }, [refreshKey]);

  const mine = useMemo(() => requests.filter((r) => r.manager === user.name), [requests, user.name]);

  const decide = async (r, action) => {
    try {
      await endpoints.decideEmployeeRequest(r.id, { action, decided_by: user.name });
      toast.success(action === "approve" ? "Approved · employee notified" : "Declined · employee notified");
      load();
      onChange?.();
    } catch {
      toast.error("Could not save decision");
    }
  };

  return (
    <div data-testid="pending-requests-card" className="glass-card rounded-3xl p-5 sm:p-6 h-full card-hover">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300 flex items-center justify-center">
          <ClipboardList className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h2 className="font-display font-bold text-[15px] tracking-tight">Employee Requests</h2>
          <p className="text-xs text-slate-500">Awaiting your decision</p>
        </div>
        <Badge variant="secondary" className="rounded-full">{mine.length}</Badge>
        <button
          data-testid="pending-view-all"
          onClick={() => navigate("/employee-requests")}
          className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 ml-2"
        >
          Open <ArrowUpRight className="h-3 w-3" />
        </button>
      </div>

      {mine.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-500">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
            <ClipboardList className="h-5 w-5 text-slate-400" />
          </div>
          No pending employee requests.
        </div>
      ) : (
        <ScrollArea className="h-[300px] pr-2">
          <ul className="space-y-2.5">
            {mine.map((r) => {
              const Icon = CATEGORY_ICON[r.category] || ClipboardList;
              return (
                <li key={r.id} data-testid={`pending-req-${r.id}`} className="rounded-2xl border border-slate-200/70 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-3">
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-xl bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-300 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold truncate">
                          {r.category}{r.leave_type ? ` · ${r.leave_type}` : ""}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5 flex-wrap">
                        <Avatar className="h-4 w-4">
                          <AvatarFallback className="brand-gradient text-white text-[8px] font-semibold">{initials(r.employee)}</AvatarFallback>
                        </Avatar>
                        <span>{r.employee}</span>
                        {r.start_date && <span>· {fmt(r.start_date)} → {fmt(r.end_date)} · {r.working_days} wd</span>}
                      </div>
                      {r.notes && <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{r.notes}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end mt-2">
                    <Button data-testid={`pending-decline-${r.id}`} size="sm" variant="outline" className="h-7 text-xs border-rose-200 text-rose-600 hover:bg-rose-50" onClick={() => decide(r, "decline")}>
                      <X className="h-3 w-3 mr-1" /> Decline
                    </Button>
                    <Button data-testid={`pending-approve-${r.id}`} size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => decide(r, "approve")}>
                      <Check className="h-3 w-3 mr-1" /> Approve
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      )}
    </div>
  );
}
